import React, { useState, useCallback, useEffect } from 'react'
import { View, Platform } from 'react-native'
import DateTimePicker from '@react-native-community/datetimepicker'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import Typography from '@ui/typography'
import { useTranslation } from 'react-i18next'
import { dateToUTC } from './utils'

interface DatePickerModalProps {
  open: boolean
  close: () => void
  value: Date | null
  onChange: (date: Date) => void
  min?: Date
  max?: Date
}

export default function DatePickerModal({
  open,
  close,
  value,
  onChange,
  min,
  max,
}: DatePickerModalProps) {
  const { t } = useTranslation(['ui'])
  const [selectedDate, setSelectedDate] = useState<Date>(value || new Date())

  useEffect(() => {
    if (open) {
      if (value) {
        setSelectedDate(dateToUTC(value))
      } else {
        setSelectedDate(new Date())
      }
    }
  }, [open, value])

  const handleChange = useCallback((event: any, date?: Date) => {
    if (date) {
      setSelectedDate(dateToUTC(date))
    }
  }, [])

  const handleConfirm = useCallback(() => {
    let finalDate = selectedDate
    if (min && finalDate < min) finalDate = min
    if (max && finalDate > max) finalDate = max
    onChange(finalDate)
    close()
  }, [selectedDate, min, max, onChange, close])

  const handleCancel = useCallback(() => {
    setSelectedDate(value || new Date())
    close()
  }, [value, close])

  return (
    <ModalBottom
      open={open}
      close={close}
      center
      disableScroll
      hideBottomSpace
      intensity={0}
    >
      <View className="flex w-full flex-col items-center gap-4 rounded-2xl border border-black/10 px-16 pb-0 pt-12">
        <Typography
          variant="XLB"
          className="text-center uppercase tracking-normal"
        >
          {t('ui:selectDate', 'Select Date')}
        </Typography>
        <View className="w-full items-center justify-center py-4">
          <DateTimePicker
            value={selectedDate}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            onChange={handleChange}
            minimumDate={min}
            maximumDate={max}
            style={{ width: '100%' }}
          />
        </View>
        <View className="flex w-full flex-row items-center justify-between gap-4 px-16 pb-12 pt-8">
          <Button
            variant="outlined"
            label={t('ui:cancel', 'Cancel')}
            onPress={handleCancel}
          />
          <Button
            variant="filled"
            label={t('ui:confirm', 'Confirm')}
            onPress={handleConfirm}
          />
        </View>
      </View>
    </ModalBottom>
  )
}
